import React, { useState, useEffect } from 'react';
import { Grid, ChevronDown } from 'lucide-react';

const SelectorSubmodulos = ({ value = [], onChange, disabled, isDarkMode, idmodulos }) => {
  const [submodulos, setSubmodulos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (idmodulos) {
      loadSubmodulos();
    } else {
      setSubmodulos([]);
      onChange([]); // Limpiar selección si no hay módulo
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idmodulos]);

  const loadSubmodulos = async () => {
    setLoading(true);
    
    try {
      const token = sessionStorage.getItem('mapasac_access_token');
      const response = await fetch('/api/submodulos?limit=100', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        // Filtrar submódulos por módulo padre
        const allSubmodulos = result.data.submodulos || [];
        const filteredSubmodulos = allSubmodulos.filter(sub => 
          sub.idmodulos === parseInt(idmodulos)
        );
        setSubmodulos(filteredSubmodulos);
      } else {
        console.error('Error cargando submódulos');
        setSubmodulos([]);
      }
    } catch (error) {
      console.error('Error de conexión:', error);
      setSubmodulos([]);
    } finally {
      setLoading(false);
    }
  };

  // Verificar si un submódulo está seleccionado
  const isSelected = (idsubmodulos) => {
    return value.some(item => item.idsubmodulos === idsubmodulos);
  };

  // Obtener los permisos de un submódulo seleccionado
  const getAcciones = (idsubmodulos) => {
    const found = value.find(item => item.idsubmodulos === idsubmodulos);
    return found ? found.acciones : 'read';
  };

  // Manejar selección/deselección de submódulo
  const handleToggleSubmodulo = (submodulo) => {
    const newValue = [...value];
    const existingIndex = newValue.findIndex(item => 
      item.idsubmodulos === submodulo.idsubmodulos
    );

    if (existingIndex >= 0) {
      // Remover si ya está seleccionado
      newValue.splice(existingIndex, 1);
    } else {
      // Agregar con permisos por defecto
      newValue.push({
        idsubmodulos: submodulo.idsubmodulos,
        acciones: 'read'
      });
    }

    onChange(newValue);
  };

  // Cambiar permisos de un submódulo seleccionado
  const handleChangeAcciones = (idsubmodulos, acciones) => {
    const newValue = value.map(item => 
      item.idsubmodulos === idsubmodulos 
        ? { ...item, acciones }
        : item
    );
    onChange(newValue);
  };

  // Crear texto del botón principal
  const getButtonText = () => {
    if (!idmodulos) {
      return 'Seleccione primero un módulo...';
    }
    if (loading) {
      return 'Cargando submódulos...';
    }
    if (submodulos.length === 0) {
      return 'No hay submódulos disponibles';
    }
    if (value.length === 0) {
      return 'Seleccionar submódulos...';
    }
    return `${value.length} submódulo(s) seleccionado(s)`;
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => !disabled && !loading && idmodulos && setIsOpen(!isOpen)}
        disabled={disabled || loading || !idmodulos}
        className={`w-full flex items-center justify-between px-3 py-2 border rounded-lg text-left transition-colors ${
          disabled || loading || !idmodulos
            ? isDarkMode 
              ? 'bg-gray-600 border-gray-500 text-gray-400 cursor-not-allowed' 
              : 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
            : isDarkMode
              ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600 focus:ring-2 focus:ring-blue-500'
              : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500'
        }`}
      >
        <div className="flex items-center">
          <Grid size={16} className={`mr-2 ${
            disabled || loading || !idmodulos ? 'text-gray-400' : 'text-blue-600'
          }`} />
          <span className="truncate">
            {getButtonText()}
          </span>
        </div>
        <ChevronDown 
          size={16} 
          className={`transition-transform ${isOpen ? 'rotate-180' : ''} ${
            disabled || loading || !idmodulos ? 'text-gray-400' : isDarkMode ? 'text-gray-300' : 'text-gray-500'
          }`} 
        />
      </button>

      {isOpen && !disabled && !loading && idmodulos && (
        <>
          {/* Overlay para cerrar el dropdown */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          ></div>
          
          {/* Dropdown */}
          <div className={`absolute z-20 w-full mt-1 border rounded-lg shadow-lg max-h-80 overflow-y-auto ${
            isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
          }`}>
            {submodulos.length === 0 ? (
              <div className={`px-3 py-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                No hay submódulos disponibles para este módulo
              </div>
            ) : (
              <div className="p-2">
                <div className={`text-xs font-medium mb-2 px-2 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  Seleccione submódulos y permisos:
                </div>
                
                {submodulos.map((submodulo) => {
                  const selected = isSelected(submodulo.idsubmodulos);
                  const acciones = getAcciones(submodulo.idsubmodulos);
                  
                  return (
                    <div
                      key={submodulo.idsubmodulos}
                      className={`p-2 rounded-lg mb-1 border ${
                        selected 
                          ? isDarkMode 
                            ? 'bg-blue-900 border-blue-600' 
                            : 'bg-blue-50 border-blue-200'
                          : isDarkMode 
                            ? 'bg-gray-800 border-gray-600' 
                            : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      {/* Checkbox y nombre del submódulo */}
                      <div className="flex items-center mb-2">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => handleToggleSubmodulo(submodulo)}
                          className="mr-2 rounded"
                        />
                        <div className="flex-1">
                          <div className={`text-sm font-medium ${
                            isDarkMode ? 'text-white' : 'text-gray-900'
                          }`}>
                            {submodulo.nombre}
                          </div>
                        </div>
                      </div>
                      
                      {/* Selector de permisos (solo si está seleccionado) */}
                      {selected && (
                        <div className="ml-6">
                          <select
                            value={acciones}
                            onChange={(e) => handleChangeAcciones(submodulo.idsubmodulos, e.target.value)}
                            className={`text-xs px-2 py-1 rounded border ${
                              isDarkMode 
                                ? 'bg-gray-700 border-gray-600 text-white' 
                                : 'bg-white border-gray-300 text-gray-900'
                            }`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <option value="read">Solo Lectura</option>
                            <option value="edit">Lectura/Escritura</option>
                            <option value="full">Acceso Completo</option>
                          </select>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SelectorSubmodulos;